<template>
  <v-dialog
    v-model="dialog"
    :max-width="modalLayout.SM"
    v-bind="modalP2FormViewport"
    :data-testid="getTestId('upload-dialog')"
    @click:outside="closeDialog"
    @close="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card>
      <CrudFormDialogHeader
        :kicker="$t('common.upload')"
        :title="dialogTitle"
        show-close
        @close="closeDialog"
      >
        <template #append>
          <div
            v-if="hasReportContext"
            class="solvit-modal-header-context d-flex align-center flex-wrap justify-end mr-2"
            :data-testid="getTestId('upload-dialog-report-context')"
          >
            <span v-if="reportInfo.siteName" class="mr-3 d-inline-flex align-center">
              <v-icon x-small class="mr-1 solvit-modal-header-context__icon">mdi-map-marker</v-icon>
              <span class="solvit-modal-header-context__primary font-weight-medium">{{ reportInfo.siteName }}</span>
            </span>
            <span v-if="reportInfo.examineDate" class="mr-3 d-inline-flex align-center text--secondary">
              <v-icon x-small class="mr-1 solvit-modal-header-context__icon">mdi-calendar</v-icon>
              <span>{{ reportInfo.examineDate }}</span>
            </span>
            <span v-if="reportInfo.reportName" class="d-inline-flex align-center text--secondary">
              <v-icon x-small class="mr-1 solvit-modal-header-context__icon">mdi-file-document-outline</v-icon>
              <span class="text-truncate" style="max-width: 12rem">{{ reportInfo.reportName }}</span>
            </span>
          </div>
        </template>
      </CrudFormDialogHeader>
      <v-card-text>
        <v-tabs
          v-model="activeTab"
          background-color="transparent"
          class="upload-dialog-tabs"
          :data-testid="getTestId('upload-tabs')"
        >
          <v-tab v-if="mode == UploadMode.upload">
            {{ $t('upload.tabRgbUpload') }}
          </v-tab>
          <v-tab v-if="mode == UploadMode.upload">
            {{ $t('upload.tabIrUpload') }}
          </v-tab>
          <v-tab v-if="mode == UploadMode.upload">
            {{ $t('upload.tabCsvUpload') }}
          </v-tab>
          <v-tab v-if="mode == UploadMode.defect">
            {{ $t('upload.tabJsonUpload') }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-if="mode == UploadMode?.upload" v-model="activeTab">
          <v-tab-item
            v-for="resource in [
              ResourceTypes.RGB,
              ResourceTypes.IR,
              ResourceTypes.CSV,
            ]"
            :key="resource"
          >
            <v-card flat>
              <v-card-text class="upload-dialog-panel d-flex flex-wrap align-center pt-4">
                <input
                  :ref="`${resource}Files`"
                  type="file"
                  multiple
                  class="d-none"
                  :accept="acceptType(resource)"
                  :data-testid="getTestId(`upload-file-input-${resource.toLowerCase()}`)"
                  @change="(e) => handleFileChange(e, resource)"
                >
                <v-btn
                  outlined
                  small
                  class="solvit-crud-outline-btn mr-3 mb-1"
                  :data-testid="getTestId(`upload-pick-${resource.toLowerCase()}`)"
                  @click="openFilePicker(resource)"
                >
                  <v-icon left small>mdi-file-plus-outline</v-icon>
                  {{ $t('files.selectFiles') }}
                </v-btn>
                <span
                  class="upload-dialog-file-summary text-body-2 mr-3 mb-1"
                  :class="hasSelectedFiles ? 'text--primary' : 'text--secondary'"
                >
                  {{ selectedFilesSummary }}
                </span>
                <v-spacer />
                <v-btn
                  outlined
                  small
                  class="solvit-crud-outline-btn solvit-crud-outline-btn--primary mb-1"
                  :data-testid="getTestId(`upload-button-${resource.toLowerCase()}`)"
                  @click="handleUpload(resource)"
                >
                  <v-icon left small>
                    mdi-upload
                  </v-icon>{{ $t('upload.uploadResourceButton', { resource }) }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
        <v-tabs-items v-else v-model="activeTab">
          <v-tab-item :key="ResourceTypes.JSON">
            <v-card flat>
              <div class="upload-dialog-hint text-body-2 font-weight-medium px-4 pt-3">
                {{ $t('upload.pairRgbIrCsvHint') }}
              </div>
              <v-card-text class="upload-dialog-panel d-flex flex-wrap align-center pt-3">
                <input
                  :ref="`jsonFiles`"
                  type="file"
                  multiple
                  class="d-none"
                  accept=".json , .zip"
                  :data-testid="getTestId('upload-file-input-json')"
                  @change="(e) => handleFileChange(e, ResourceTypes.JSON)"
                >
                <v-btn
                  outlined
                  small
                  class="solvit-crud-outline-btn mr-3 mb-1"
                  :data-testid="getTestId('upload-pick-json')"
                  @click="openFilePicker('json')"
                >
                  <v-icon left small>mdi-file-plus-outline</v-icon>
                  {{ $t('files.selectFiles') }}
                </v-btn>
                <span
                  class="upload-dialog-file-summary text-body-2 mr-3 mb-1"
                  :class="hasSelectedFiles ? 'text--primary' : 'text--secondary'"
                >
                  {{ selectedFilesSummary }}
                </span>
                <v-spacer />
                <v-btn
                  outlined
                  small
                  class="solvit-crud-outline-btn solvit-crud-outline-btn--primary mb-1"
                  :data-testid="getTestId('upload-button-json')"
                  @click="handleUpload(ResourceTypes.JSON)"
                >
                  <v-icon left small>
                    mdi-upload
                  </v-icon>{{ $t('imageTypes.uploadJson') }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import JSZip from 'jszip';
import { getTestId } from '@/utils/testUtils';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';
import {
  ZIP_BROWSER_VALIDATE_MAX_BYTES,
  ZIP_UPLOAD_MAX_BYTES
} from '@/store/modules/upload/uploadLimits';
import { normalizeReportInfo } from '@/utils/reportUtils';

const ResourceTypes = {
  RGB: 'RGB',
  IR: 'IR',
  CSV: 'CSV',
  JSON: 'JSON'
};

const UploadMode = {
  upload: 'upload',
  defect: 'defect'
};

export default {
  name: 'UploadDialog',
  components: {
    CrudFormDialogHeader
  },
  props: {
    reportId: {
      type: Number,
      required: true
    },
    dialog: {
      type: Boolean,
      required: true
    },
    mode: {
      type: String,
      required: true
    },
    editedItem: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  emits: ['close', 'update:dialog'],
  data() {
    return {
      modalLayout: MODAL_MAX_WIDTH,
      modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
      activeTab: 0,
      snackbar: false,
      snackbarCloseBtn: false,
      ResourceTypes: ResourceTypes,
      UploadMode: UploadMode,
      selectedFiles: {}
    };
  },
  computed: {
    ...mapGetters({
      uploadProgress: 'uploadfile/uploadProgress',
      currentStep: 'steps/currentStep',
      uploadInProgress: 'uploadfile/uploadInProgress'
    }),
    dialogTitle() {
      switch (this.$props.mode) {
      case 'upload':
        return this.$t('upload.dialogTitleUpload');
      case 'defect':
        return this.$t('upload.dialogTitleDefect');
      case 'analyze':
        return this.$t('upload.dialogTitleAnalyze');
      default:
        return this.$t('upload.dialogTitleUpload');
      }
    },
    reportInfo() {
      if (this.editedItem?.reportInfo) {
        return this.editedItem.reportInfo;
      }
      if (this.editedItem?.name || this.editedItem?.site) {
        return normalizeReportInfo(this.editedItem, 'table');
      }
      return null;
    },
    hasReportContext() {
      const info = this.reportInfo;
      return Boolean(info?.siteName || info?.reportName || info?.examineDate);
    },
    hasSelectedFiles() {
      return Array.isArray(this.selectedFiles) && this.selectedFiles.length > 0;
    },
    selectedFilesSummary() {
      if (!this.hasSelectedFiles) {
        return this.$t('files.noSelected');
      }
      const files = this.selectedFiles;
      if (files.length === 1) {
        return files[0].name;
      }
      return `${files[0].name} (+${files.length - 1})`;
    },
    totalFileNum() {
      return () => {
        const files = this.selectedFiles || [];
        return files.length;
      };
    },
    totalFileSize() {
      return () => {
        const files = this.selectedFiles || [];
        const totalSize = files.reduce((acc, file) => acc + file.size, 0);
        if (totalSize > 1e9) {
          return `${(totalSize / 1e9).toFixed(2)} GB`;
        } else if (totalSize > 1e6) {
          return `${(totalSize / 1e6).toFixed(2)} MB`;
        } else if (totalSize > 1e3) {
          return `${(totalSize / 1e3).toFixed(2)} KB`;
        } else {
          return `${totalSize} bytes`;
        }
      };
    },
    snackbarErrorText() {
      if (this.uploadErrors.length > 0) {
        const errorCount = this.uploadErrors.reduce((acc, cur) => {
          acc[cur.resource] = acc[cur.resource] ? acc[cur.resource] + 1 : 1;
          return acc;
        }, {});
        const details = Object.keys(errorCount)
          .map((key) =>
            this.$t('upload.errorCountPerResource', {
              resource: key,
              count: errorCount[key]
            })
          )
          .join(', ');
        return this.$t('upload.uploadErrorSummary', { details });
      }
      return '';
    }
  },
  watch: {
    selectedFiles(newFiles) {
      if (Object.keys(newFiles).length > 0) {
        console.log('Selected files changed:', newFiles);
      }
    },
    activeTab(newValue, oldValue) {
      // tab 변경시
      if (newValue == null || oldValue == null) return;
      //탭변경시 기존 input ref value 초기화
      let resource = '';
      switch (oldValue) {
      case 0:
        resource = 'RGB';
        break;
      case 1:
        resource = 'IR';
        break;
      case 2:
        resource = 'CSV';
        break;
      default:
        resource = 'error';
        break;
      }
      if (resource == 'error') return;
      //이전탭의 값 초기화
      const inputEl = this.getFileInputEl(resource);
      if (inputEl) {
        inputEl.value = null;
      }
      this.selectedFiles = [];
    },
    totalProgress(newValue) {
      if (this.uploadErrors.length > 0) {
        this.snackbarCloseBtn = true;
      }
      setTimeout(() => {
        if (newValue === 100 && this.uploadErrors.length == 0) {
          this.snackbar = false;
          this.clearFiles();
        }
      }, 8000);
    }
  },
  methods: {
    ...mapActions({
      resetUpload: 'uploadfile/resetUpload',
      uploadFiles: 'uploadfile/uploadFiles'
      // triggerSnackbar: "uploadsnackbar/triggerSnackbar",
    }),
    async handleUpload(resourceType) {
      const date = new Date();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ms = date.getMilliseconds();
      const uploadId = `${hours}:${String(minutes).padStart(2, '0')}_${ms}`;
      console.log(
        `테스트 handleUpload for ${resourceType}`,
        this.$props.reportId,
        this.selectedFiles ?? [],
        uploadId
      );
      const files = this.selectedFiles;
      if (files.length === 0) {
        alert(this.$t('upload.selectFilesFirst'));
        return;
      }
      try {
        const hasDir = await this.validateZipFile(files, resourceType);
        if (hasDir) {
          return;
        }
      } catch (err) {
        console.error('압축파일 검증 중 에러 발생 ', err);
      }

      // this.triggerSnackbar({
      //   uploadId,
      // });
      const reportName = this.$props.editedItem?.name;
      // detail 복구
      this.uploadFiles({
        uploadId,
        reportName,
        reportId: this.$props.reportId,
        resourceType,
        selectedFiles: files
      });
      //업로드 요청 보낸후에 바로 창닫기
      this.closeDialog();
    },
    closeDialog() {
      this.$emit('close');
      this.selectedFiles = []; // 다이얼로그 닫을 때 파일 초기화
    },
    getFileInputEl(resourceKey) {
      const key = resourceKey === 'json' || resourceKey === 'JSON' ? 'jsonFiles' : `${resourceKey}Files`;
      const ref = this.$refs[key];
      if (!ref) {
        return null;
      }
      return Array.isArray(ref) ? ref[0] : ref;
    },
    openFilePicker(resourceKey) {
      const inputEl = this.getFileInputEl(resourceKey);
      if (inputEl) {
        inputEl.click();
      }
    },
    handleFileChange(event, resource) {
      console.log(
        '테스트 handleFileChange',
        Array.from(event.target.files),
        resource
      );
      const filesArray = Array.from(event.target.files);
      if (this.$props.reportId == null) {
        alert(this.$t('upload.createReportFirst'));
        return;
      }
      this.selectedFiles = filesArray;
    },
    /**
     * @@ 압축파일 폴더포함유무
     * @param files File[]
     * @param resourceType ResourceTypes
     */
    isZipFile(file) {
      const name = (file?.name || '').toLowerCase();
      return name.endsWith('.zip');
    },
    /**
     * ZIP만 검증. 100MB 이하: JSZip으로 폴더·확장자 확인.
     * 100MB~5GB: 브라우저 검증 생략 후 업로드(서버 검증). 5GB 초과: 거부.
     */
    async validateZipFile(files, resourceType) {
      let cantUpload = false;
      for (const file of files) {
        if (!this.isZipFile(file)) {
          continue;
        }
        if (file.size > ZIP_UPLOAD_MAX_BYTES) {
          const maxGb = (ZIP_UPLOAD_MAX_BYTES / (1024 * 1024 * 1024)).toFixed(0);
          alert(this.$t('upload.zipExceedsMaxUploadSize', { maxGb }));
          return true;
        }
        if (file.size > ZIP_BROWSER_VALIDATE_MAX_BYTES) {
          continue;
        }
        const zip = new JSZip();
        const unzip = await zip.loadAsync(file);
        for (const zipChip of Object.values(unzip?.files)) {
          if (zipChip.dir) {
            cantUpload = true;
            alert(this.$t('upload.zipFolderNotAllowed'));
            break;
          } else {
            //각파일의 확장자명 체크
            if (this.checkFileExtension(zipChip, resourceType)) continue;
            else {
              cantUpload = true;
              if (resourceType === ResourceTypes.JSON)
                alert(this.$t('upload.zipUnsupportedExtensionJson'));
              else if (
                resourceType == ResourceTypes.RGB ||
                resourceType == ResourceTypes.IR
              )
                alert(this.$t('upload.zipUnsupportedExtensionJpg'));
              else
                alert(this.$t('upload.zipUnsupportedExtensionCsv'));
              break;
            }
          }
        }
        if (cantUpload) break;
      }
      return cantUpload;
    },
    //같은 확장자명 파일만 업로드 가능하도록 체크
    /**
     * @param file File
     * @param resourceType ResourceTypes
     */
    checkFileExtension(file, resourceType) {
      const fileExtensionName = file.name.split('.').pop();
      switch (resourceType) {
      case ResourceTypes.IR:
        return fileExtensionName === 'jpg' || fileExtensionName === 'jpeg';
      case ResourceTypes.RGB:
        return fileExtensionName === 'jpg' || fileExtensionName === 'jpeg';
      case ResourceTypes.CSV:
        return fileExtensionName === 'csv';
      case ResourceTypes.JSON:
        return fileExtensionName === 'json';
      default:
        return false;
      }
    },
    clearFiles(resource) {
      this.resetUpload({ reportId: this.$props.reportId, resource });
      this.selectedFiles = [];
    },
    acceptType(resource) {
      switch (resource) {
      case ResourceTypes.IR:
        return 'image/jpg, image/jpeg, application/zip';
      case ResourceTypes.RGB:
        return 'image/jpg, image/jpeg, application/zip';
      case ResourceTypes.CSV:
        return 'text/csv, application/zip';
      default:
        return 'image/jpg, image/jpeg, application/zip';
      }
    },
    getTestId(testId) {
      return getTestId(testId);
    }
  }
};
</script>

<style scoped>
.upload-dialog-tabs ::v-deep .v-tab {
  color: var(--v-secondary-base) !important;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}
.upload-dialog-tabs ::v-deep .v-tab--active {
  color: var(--v-primary-base) !important;
  font-weight: 600;
}
.upload-dialog-tabs ::v-deep .v-tabs-slider {
  background-color: var(--v-primary-base);
}

.upload-dialog-panel {
  gap: 4px 0;
}

.upload-dialog-file-summary {
  max-width: min(280px, 100%);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.35;
}

/* secondary보다 한 단계 밝게 — 다크에서 «선택된 파일 없음» 가독성 */
.upload-dialog-file-summary.text--secondary {
  color: color-mix(in srgb, var(--v-text-base) 68%, transparent) !important;
}

.upload-dialog-hint {
  color: var(--v-warning-base);
}

.upload-button,
.refresh-button {
  background-color: var(--v-secondary-base);
  color: var(--v-text-base);
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  border: none;
  margin: 0.25rem;
  transition: background-color var(--solvit-motion-duration) var(--solvit-motion-easing),
    color var(--solvit-motion-duration) var(--solvit-motion-easing);
}

.upload-button:hover,
.refresh-button:hover {
  filter: brightness(0.92);
  color: var(--v-text-base);
}

.upload-button:disabled,
.refresh-button:disabled {
  background-color: var(--v-surface-base);
  color: var(--v-text-base);
  cursor: not-allowed;
}

.error-file {
  border-top: 1px dotted var(--v-error-base);
  border-bottom: 1px dotted var(--v-error-base);
  margin: 0.25rem 0;
  color: var(--v-error-base);
}

.upload-snackbar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.me-2 {
  margin-right: 0.5rem;
}

.text-grey-darken-3 {
  color: var(--v-text-base);
}

.mx-2 {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.custom-file-input .v-input__control .v-input__slot {
  background-color: var(--v-surface-base);
  box-shadow: inset 0px 0px 5px color-mix(in srgb, var(--v-text-base) 30%, transparent);
  border-radius: 4px;
  transition: opacity var(--solvit-motion-duration) var(--solvit-motion-easing),
    transform var(--solvit-motion-duration) var(--solvit-motion-easing);
  cursor: pointer !important;
}
</style>
